import { useState, useContext } from 'react'
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded'
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import { useNls } from '../../../../nls/nlsProvider'
import { type ChartDescriptor, type ChartRecord } from '../../charts'
import { Button } from '../../../Common/Button'
import { StatusChip } from '../../../Common'
import type { AnalysisViewMode } from '../../../../types/analysis'
import type { Workspace } from '../../../../context/WorkspaceContext'
import { WorkspaceContext } from '../../../../context/workspaceContextCore'
import { roleCanPerform } from '../../../../utils/workspacePermissions'
import type { WorkspaceRole } from '../../../../types/workspace'
import { exportModuleChartPng, exportModuleCsv } from '../../../../services/analysisExportService'
import { createChart } from '../../../../services/chartService'
import { useToast } from '../../../../context/useToast'
import { SendToChatMenu } from './SendToChatMenu'
import { mapAnalysisChartToSpec } from '../analysisChartMapping'

export const ExportSharePanel = ({
  collapsed,
  descriptor,
  records,
  loadingRecords,
  viewMode,
  workspace,
  snapshotId,
  isReducedViewer = false,
  canManage: canManageProp,
  getSvg,
  onOpenReports,
}: {
  collapsed: boolean
  chartId?: string
  descriptor: ChartDescriptor
  records: ChartRecord[]
  loadingRecords: boolean
  viewMode: AnalysisViewMode
  workspace?: Workspace
  snapshotId?: string
  isReducedViewer?: boolean
  canManage?: boolean
  getSvg: () => SVGSVGElement | null
  onOpenReports?: () => void
}) => {
  const { t } = useNls()
  const { showToast } = useToast()
  const workspaceContext = useContext(WorkspaceContext)
  const activeRole =
    workspaceContext?.activeRole ??
    (workspaceContext?.rolesByWorkspaceId?.get(workspace?.id ?? '') as WorkspaceRole | undefined)
  const canManage =
    canManageProp ?? (activeRole ? roleCanPerform(activeRole, 'EDITOR') : !isReducedViewer)

  const [savingToLibrary, setSavingToLibrary] = useState(false)
  const savableSpec = mapAnalysisChartToSpec(descriptor)
  const viewerGateTitle = isReducedViewer ? t('NLSAnalysisViewerGateTooltip') : undefined
  if (collapsed) {
    return <StatusChip tone="info" size="sm">{t('NLSAnalysisChartExportShare')}</StatusChip>
  }
  return (
    <div className="analysisChartExportActions">
      <span title={viewerGateTitle ?? (viewMode !== 'LIVE' ? t('NLSAnalysisExportCsvDisabledHistoric') : undefined)}>
        <Button variant="secondary" size="sm" icon={<DownloadRoundedIcon />} disabled={isReducedViewer || viewMode !== 'LIVE' || loadingRecords || records.length === 0 || !workspace} title={viewerGateTitle} onClick={() => {
          if (!workspace) return
          void exportModuleCsv({ workspace, moduleId: descriptor.id, moduleTitle: descriptor.title, records, snapshotId, viewMode }).catch(() => showToast(t('NLSAnalysisExportFailed'), 'error'))
        }}>
          {t('NLSAnalysisExportModuleCsv')}
        </Button>
      </span>
      <Button variant="secondary" size="sm" icon={<ImageOutlinedIcon />} disabled={isReducedViewer || !workspace} title={viewerGateTitle ?? (!workspace ? t('NLSAnalysisExportPngUnavailable') : undefined)} onClick={() => {
        const svg = getSvg()
        if (!svg) { showToast(t('NLSAnalysisExportPngUnavailable'), 'warning'); return }
        if (!workspace) return
        void exportModuleChartPng({ workspace, moduleId: descriptor.id, moduleTitle: descriptor.title, svgElement: svg, viewMode, snapshotId }).catch(() => showToast(t('NLSAnalysisExportPngFailed'), 'error'))
      }}>
        {t('NLSAnalysisExportModulePng')}
      </Button>
      <SendToChatMenu workspace={workspace} descriptor={descriptor} records={records} viewMode={viewMode} snapshotId={snapshotId} isReducedViewer={isReducedViewer} />
      {canManage && savableSpec && workspace && (
        <Button
          variant="secondary"
          size="sm"
          icon={<BookmarkBorderRoundedIcon />}
          disabled={savingToLibrary}
          data-testid="analysis-save-to-library-btn"
          onClick={() => {
            setSavingToLibrary(true)
            void createChart(workspace.id, savableSpec)
              .then(() => showToast(t('NLSChartsSaveToLibrarySuccess'), 'success'))
              .catch((err: unknown) => {
                const errObj = err as { details?: { code?: string; limit?: number }; message?: string }
                if (errObj?.details?.code === 'charts.limit' || errObj?.message?.includes('limit')) {
                  const limit = errObj?.details?.limit
                  if (limit === 5) {
                    showToast(t('NLSChartsLimitReachedFree'), 'error')
                  } else if (limit === 50) {
                    showToast(t('NLSChartsLimitReachedPaid'), 'error')
                  } else {
                    showToast(t('NLSChartsLimitReachedGeneric'), 'error')
                  }
                } else {
                  console.error('Failed to save chart to library:', err)
                  showToast(t('NLSChartsSaveFailed'), 'error')
                }
              })
              .finally(() => setSavingToLibrary(false))
          }}
        >
          {savingToLibrary ? t('NLSCommonEntityFormSaving') : t('NLSChartsSaveToLibrary')}
        </Button>
      )}
      <Button variant="tertiary" size="sm" icon={<BarChartRoundedIcon />} onClick={onOpenReports}>
        {t('NLSAnalysisChartActionOpenReports')}
      </Button>
      <Button variant="tertiary" size="sm" icon={<ContentCopyRoundedIcon />} disabled title={t('NLSAnalysisChartCopyLinkDisabled')}>
        {t('NLSAnalysisChartActionCopyLink')}
      </Button>
    </div>
  )
}
